import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, ErrorMessage } from 'formik';
import { useRouteMatch } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import InputLabel from '@material-ui/core/InputLabel';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import { makeStyles } from '@material-ui/styles';
import ReCAPTCHA from 'react-google-recaptcha';
import helpFormSchema from './helpFormSchema';
import LoadingButton from '../LoadingButton';
import appNames from '../../constants/appNames';
import businessLines from '../../constants/businessLines';

const useStyles = makeStyles(theme => ({
  box: {
    minHeight: 100,
    marginBottom: theme.spacing(1),
  },
  multiFieldParent: {
    display: 'flex',
    alignItems: 'baseline',
  },
  multiFieldChild: {
    'flexGrow': 1,
    'marginBottom': theme.spacing(1),
    'minHeight': theme.spacing(10),
    'marginRight': theme.spacing(2),
    '&:last-child': {
      marginRight: 0,
    },
  },
  textField: {
    marginBottom: theme.spacing(1),
    minHeight: theme.spacing(10),
  },
  textFieldDescription: {
    marginBottom: theme.spacing(1),
    minHeight: 250,
  },
  textFieldEnd: {
    marginBottom: theme.spacing(2),
  },
  uploadButton: {
    marginBottom: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
  errorMessage: {
    color: theme.palette.error.main,
  },
}));

export default function HelpForm({ handleSubmit }) {
  const fileRef = React.createRef();
  const [isFileAttached, setIsFileAttached] = useState(false);
  const [filename, setFilename] = useState('Select file');
  const match = useRouteMatch();
  let { appName, lob } = match.params;
  appName = appNames.includes(appName) ? appName : '';
  lob = businessLines.includes(lob) ? lob : '';

  const classes = useStyles();
  const initialValues = {
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    description: '',
    application: appName || 'EpiPortal',
    businessLine: lob || 'Medicare',
    priority: 1,
    recaptcha: null,
  };

  const removeFile = (event, setFieldValue) => {
    setFieldValue('file', null);
    setFilename('Select file');
    setIsFileAttached(false);
    event.preventDefault();
  };

  const updateFile = setFieldValue => {
    const file = fileRef.current?.files[0];
    if (file) {
      setFieldValue('file', file);
      setFilename(fileRef.current.files[0].name);
      setIsFileAttached(true);
    } else {
      removeFile(null, setFieldValue);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={helpFormSchema}
      onSubmit={values => handleSubmit(values)}
    >
      {({ values, errors, handleChange, setFieldValue, isSubmitting, submitCount }) => (
        <Form>
          <div className={classes.multiFieldParent}>
            <TextField
              id="firstName"
              className={classes.multiFieldChild}
              name="firstName"
              label="First Name"
              value={values.firstName}
              onChange={handleChange}
              variant="outlined"
              helperText={submitCount ? errors.firstName : ''}
              error={Boolean(submitCount && errors.firstName)}
            />
            <TextField
              id="lastName"
              className={classes.multiFieldChild}
              name="lastName"
              label="Last Name"
              value={values.lastName}
              onChange={handleChange}
              variant="outlined"
              helperText={submitCount ? errors.lastName : ''}
              error={Boolean(submitCount && errors.lastName)}
            />
          </div>
          <TextField
            id="email"
            className={classes.textField}
            name="email"
            label="Email"
            value={values.email}
            onChange={handleChange}
            variant="outlined"
            helperText={submitCount ? errors.email : ''}
            error={Boolean(submitCount && errors.email)}
            fullWidth
          />
          <div className={classes.multiFieldParent}>
            <TextField
              id="application"
              className={classes.multiFieldChild}
              select
              name="application"
              label="Application"
              value={values.application}
              onChange={handleChange}
              variant="outlined"
              disabled={Boolean(appName)}
            >
              <MenuItem value="EpiPortal">EpiPortal</MenuItem>
              <MenuItem value="EpiAnalyst">EpiAnalyst</MenuItem>
              <MenuItem value="EpiConnect">EpiConnect</MenuItem>
              <MenuItem value="EpiVault">EpiVault</MenuItem>
              <MenuItem value="EpiVirtualCare">EpiVirtualCare</MenuItem>
            </TextField>
            {values.application === 'EpiAnalyst' ? (
              <TextField
                id="businessLine"
                className={classes.multiFieldChild}
                select
                name="businessLine"
                label="Line of Business"
                value={values.businessLine}
                onChange={handleChange}
                variant="outlined"
                disabled={Boolean(lob)}
              >
                <MenuItem value="Medicare">Medicare</MenuItem>
                <MenuItem value="Medicaid">Medicaid</MenuItem>
              </TextField>
            ) : (
              <></>
            )}
          </div>
          <TextField
            id="subject"
            className={classes.textField}
            name="subject"
            label="Subject"
            value={values.subject}
            onChange={handleChange}
            variant="outlined"
            helperText={submitCount ? errors.subject : ''}
            error={Boolean(submitCount && errors.subject)}
            fullWidth
          />
          <TextField
            id="description"
            className={classes.textFieldDescription}
            name="description"
            label="Description"
            value={values.description}
            onChange={handleChange}
            multiline
            rows="10"
            variant="outlined"
            helperText={submitCount ? errors.description : ''}
            error={Boolean(submitCount && errors.description)}
            fullWidth
          />
          <TextField
            id="priority"
            className={classes.textFieldEnd}
            select
            name="priority"
            label="Priority"
            value={values.priority}
            onChange={handleChange}
            variant="outlined"
            fullWidth
          >
            <MenuItem value={1}>Low</MenuItem>
            <MenuItem value={2}>Medium</MenuItem>
            <MenuItem value={3}>High</MenuItem>
            <MenuItem value={4}>Urgent</MenuItem>
          </TextField>
          <div className={classes.multiFieldParent}>
            {isFileAttached ? (
              <Button
                className={classes.uploadButton}
                onClick={event => removeFile(event, setFieldValue)}
                variant="contained"
                component="label"
                color="secondary"
                size="small"
              >
                Remove File
              </Button>
            ) : (
              <Button
                className={classes.uploadButton}
                variant="contained"
                component="label"
                size="small"
              >
                Upload File
                <input
                  id="file"
                  name="file"
                  type="file"
                  ref={fileRef}
                  onChange={() => updateFile(setFieldValue)}
                  style={{ display: 'none' }}
                />
              </Button>
            )}
            <InputLabel>{filename}</InputLabel>
          </div>
          <Box className={classes.box}>
            <ReCAPTCHA
              sitekey={process.env.REACT_APP_RECAPTCHA_KEY}
              onChange={response => setFieldValue('recaptcha', response)}
            />
            <ErrorMessage className={classes.errorMessage} name="recaptcha" component="span" />
          </Box>
          <LoadingButton
            type="submit"
            isSubmitting={isSubmitting}
            variant="contained"
            color="primary"
          >
            Submit
          </LoadingButton>
        </Form>
      )}
    </Formik>
  );
}

HelpForm.defaultProps = {
  handleSubmit: () => {},
};

HelpForm.propTypes = {
  handleSubmit: PropTypes.func,
};
